/**
 * =============================================================================
 * CHAT WINDOW COMPONENT
 * =============================================================================
 * 
 * The main chat interface with:
 * - Pinned alert card at top (reference point)
 * - Chat messages below (logs, action cards, results)
 * 
 * API INTEGRATION POINTS:
 * - Line 180: Action click → fetchActionDetails() → shows ActionCard
 * - Line 220: Action confirm → executeAlertAction() → shows result
 * - Line 260: Snooze → snoozeAlert() → logs in chat
 * - Line 290: Delete → deleteAlert() → closes chat
 * - Line 320: Edit iframe confirm → submitIframeEdit() → logs result
 * 
 * All API functions are in api.ts with clear TODO markers for backend integration.
 */

import { Box, Stack, ActionIcon, Text, Group, Modal, Button, Menu, Divider } from '@mantine/core';
import { ArrowLeft, DotsThreeVertical, Trash, ClockCounterClockwise, Pencil } from '@phosphor-icons/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { fetchActionDetails, executeAlertAction, snoozeAlert, deleteAlert, submitIframeEdit, ignoreAlert } from './api';
import PinnedAlertCard from './PinnedAlertCard';
import ActionDetailCard from './ActionDetailCard';
import ChatLogEntry from './ChatLogEntry';
import IframeEditModal from './IframeEditModal';

import type { AlertItem, AlertAction, ActionDetails, ChatMessage } from './types';
import { SNOOZE_OPTIONS, ChatLogType } from './types';

interface ChatWindowProps {
    /** The alert being viewed */
    alert: AlertItem;
    /** Called when user clicks back */
    onBack: () => void;
    /** Called when alert is deleted or action completed (to refresh list) */
    onAlertUpdated: (alertId: string, action: 'deleted' | 'completed' | 'snoozed' | 'ignored') => void;
    /** Current user info for logs */
    currentUser?: { name: string };
}

// =============================================================================
// HELPER: Generate unique ID
// =============================================================================
const generateId = () => `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// =============================================================================
// COMPONENT
// =============================================================================

const ChatWindow: React.FC<ChatWindowProps> = memo(({
    alert,
    onBack,
    onAlertUpdated,
    currentUser = { name: 'You' },
}) => {
    // =========================================================================
    // STATE
    // =========================================================================
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Action confirmation state
    const [pendingAction, setPendingAction] = useState<AlertAction | null>(null);
    const [pendingActionDetails, setPendingActionDetails] = useState<ActionDetails | null>(null);
    
    // Iframe edit state
    const [iframeAction, setIframeAction] = useState<AlertAction | null>(null);
    const [iframeUrl, setIframeUrl] = useState<string | null>(null);
    const [iframeActionDetails, setIframeActionDetails] = useState<ActionDetails | null>(null);

    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    // =========================================================================
    // LOG HELPERS
    // =========================================================================
    
    const addLog = useCallback((logType: ChatLogType, message: string) => {
        const logMessage: ChatMessage = {
            id: generateId(),
            type: 'log',
            logType,
            message,
            user: currentUser.name,
            timestamp: new Date().toISOString(),
        };
        setChatMessages((prev) => [...prev, logMessage]);
    }, [currentUser.name]);

    // =========================================================================
    // ACTION CLICK HANDLER - Line 180
    // API: fetchActionDetails()
    // =========================================================================
    
    /**
     * Called when user clicks an action button on the pinned alert
     * Fetches action details and shows confirmation card
     */
    const handleActionClick = useCallback(async (action: AlertAction) => {
        addLog(ChatLogType.ACTION_SELECTED, `Selected action: ${action.label}`);
        setIsLoading(true);

        try {
            // ================================================================
            // API CALL: fetchActionDetails()
            // Location: api.ts, Line ~190
            // Endpoint: GET /alerts/:alertId/actions/:actionId/details
            // ================================================================
            const details = await fetchActionDetails(alert.id, action.id);
            
            setPendingAction(action);
            setPendingActionDetails(details);
        } catch (error) {
            console.error('Failed to fetch action details:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to load action details. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [alert.id, addLog]);

    // =========================================================================
    // ACTION CONFIRM HANDLER - Line 220
    // API: executeAlertAction()
    // =========================================================================
    
    /**
     * Called when user confirms an action
     * Executes the action and shows result
     */
    const handleConfirmAction = useCallback(async () => {
        if (!pendingAction || !pendingActionDetails) return;

        addLog(ChatLogType.ACTION_CONFIRMED, `Confirmed: ${pendingAction.label}`);
        setIsLoading(true);

        try {
            // ================================================================
            // API CALL: executeAlertAction()
            // Location: api.ts, Line ~250
            // Endpoint: POST /alerts/:alertId/actions/:actionId/execute
            // ================================================================
            const result = await executeAlertAction(alert.id, pendingAction);
            
            // Add result to chat
            const resultMessage: ChatMessage = {
                id: generateId(),
                type: 'action_result',
                action: pendingAction,
                success: result.success,
                message: result.message,
                timestamp: new Date().toISOString(),
            };
            setChatMessages((prev) => [...prev, resultMessage]);

            if (result.success) {
                onAlertUpdated(alert.id, 'completed');
            }
        } catch (error) {
            console.error('Failed to execute action:', error);
            addLog(ChatLogType.SYSTEM, 'Action failed. Please try again.');
        } finally {
            setIsLoading(false);
            setPendingAction(null);
            setPendingActionDetails(null);
        }
    }, [pendingAction, pendingActionDetails, alert.id, addLog, onAlertUpdated]);

    // =========================================================================
    // ACTION REJECT HANDLER
    // =========================================================================
    
    const handleRejectAction = useCallback(() => {
        addLog(ChatLogType.ACTION_REJECTED, `Cancelled: ${pendingAction?.label}`);
        setPendingAction(null);
        setPendingActionDetails(null);
    }, [pendingAction, addLog]);

    // =========================================================================
    // EDIT ACTION HANDLER - Opens iframe
    // =========================================================================
    
    const handleEditAction = useCallback(() => {
        if (!pendingAction || !pendingActionDetails) return;
        
        addLog(ChatLogType.EDIT_STARTED, `Editing: ${pendingAction.label}`);
        setIframeAction(pendingAction);
        setIframeActionDetails(pendingActionDetails); // Store action details for iframe
        setIframeUrl(pendingActionDetails.editUrl || `/edit/action/${alert.id}/${pendingAction.id}`);
        setPendingAction(null);
        setPendingActionDetails(null);
    }, [pendingAction, pendingActionDetails, alert.id, addLog]);

    // =========================================================================
    // SNOOZE HANDLER - Line 260
    // API: snoozeAlert()
    // =========================================================================
    
    const handleSnooze = useCallback(async (minutes: number) => {
        const option = SNOOZE_OPTIONS.find((o) => o.value === minutes);
        addLog(ChatLogType.ALERT_SNOOZED, `Snoozed for ${option?.label || minutes + ' minutes'}`);
        setIsLoading(true);

        try {
            // ================================================================
            // API CALL: snoozeAlert()
            // Location: api.ts, Line ~310
            // Endpoint: POST /alerts/:alertId/snooze
            // ================================================================
            const result = await snoozeAlert(alert.id, minutes);
            
            if (result.success) {
                addLog(ChatLogType.SYSTEM, result.message);
                onAlertUpdated(alert.id, 'snoozed');
            }
        } catch (error) {
            console.error('Failed to snooze:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to snooze. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [alert.id, addLog, onAlertUpdated]);

    // =========================================================================
    // DELETE HANDLER - Line 290
    // API: deleteAlert()
    // =========================================================================
    
    const handleDelete = useCallback(async () => {
        setIsDeleting(true);

        try {
            // ================================================================
            // API CALL: deleteAlert()
            // Location: api.ts, Line ~360
            // Endpoint: DELETE /alerts/:alertId
            // ================================================================
            const result = await deleteAlert(alert.id);
            
            if (result.success) {
                onAlertUpdated(alert.id, 'deleted');
                onBack();
            }
        } catch (error) {
            console.error('Failed to delete:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to delete. Please try again.');
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [alert.id, addLog, onAlertUpdated, onBack]);

    // =========================================================================
    // IGNORE HANDLER
    // API: ignoreAlert()
    // =========================================================================
    
    const handleIgnore = useCallback(async () => {
        addLog(ChatLogType.SYSTEM, 'Ignoring alert...');
        setIsLoading(true);

        try {
            const result = await ignoreAlert(alert.id);
            
            if (result.success) {
                addLog(ChatLogType.SYSTEM, 'Alert ignored');
                onAlertUpdated(alert.id, 'deleted'); // Treat as deleted for UI refresh
                onBack();
            }
        } catch (error) {
            console.error('Failed to ignore:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to ignore. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [alert.id, addLog, onAlertUpdated, onBack]);

    // =========================================================================
    // IFRAME EDIT HANDLERS - Line 320
    // API: submitIframeEdit()
    // =========================================================================
    
    const handleIframeConfirm = useCallback(async (editedData: Record<string, unknown>) => {
        if (!iframeAction) return;

        addLog(ChatLogType.EDIT_COMPLETED, `Edited and confirmed: ${iframeAction.label}`);
        setIsLoading(true);

        try {
            // ================================================================
            // API CALL: submitIframeEdit()
            // Location: api.ts, Line ~400
            // Endpoint: PUT /alerts/:alertId (or action.apiEndpoint)
            // ================================================================
            const result = await submitIframeEdit(alert.id, iframeAction, editedData);
            
            const resultMessage: ChatMessage = {
                id: generateId(),
                type: 'action_result',
                action: iframeAction,
                success: result.success,
                message: result.message,
                timestamp: new Date().toISOString(),
            };
            setChatMessages((prev) => [...prev, resultMessage]);

            if (result.success) {
                onAlertUpdated(alert.id, 'completed');
            }
        } catch (error) {
            console.error('Failed to submit edit:', error);
            addLog(ChatLogType.SYSTEM, 'Failed to save changes. Please try again.');
        } finally {
            setIsLoading(false);
            setIframeAction(null);
            setIframeUrl(null);
            setIframeActionDetails(null);
        }
    }, [iframeAction, alert.id, addLog, onAlertUpdated]);

    const handleIframeCancel = useCallback(() => {
        addLog(ChatLogType.EDIT_CANCELLED, 'Edit cancelled');
        setIframeAction(null);
        setIframeUrl(null);
        setIframeActionDetails(null);
    }, [addLog]);

    // =========================================================================
    // RENDER
    // =========================================================================

    return (
        <Stack className="h-full gap-0">
            {/* Header */}
            <Box className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3">
                <Group className="justify-between">
                    <Group className="gap-3 flex-1 min-w-0">
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="md"
                            onClick={onBack}
                        >
                            <ArrowLeft size={18} weight="bold" />
                        </ActionIcon>
                        <Stack className="gap-0 flex-1 min-w-0">
                            <Text className="text-sm font-semibold text-gray-900 truncate">
                                {alert.title}
                            </Text>
                            <Text className="text-xs text-gray-500">
                                {alert.alertTypeLabel}
                            </Text>
                        </Stack>
                    </Group>

                    {/* Edit Menu (Delete/Snooze) */}
                    <Menu shadow="md" width={180} position="bottom-end" withArrow>
                        <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" size="md">
                                <DotsThreeVertical size={18} weight="bold" />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Label>Snooze</Menu.Label>
                            {SNOOZE_OPTIONS.map((option) => (
                                <Menu.Item
                                    key={option.value}
                                    leftSection={<ClockCounterClockwise size={14} />}
                                    onClick={() => handleSnooze(option.value)}
                                >
                                    {option.label}
                                </Menu.Item>
                            ))}
                            <Menu.Divider />
                            <Menu.Item
                                color="red"
                                leftSection={<Trash size={14} />}
                                onClick={() => setShowDeleteModal(true)}
                            >
                                Delete Alert
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Box>

            {/* Chat Content */}
            <Box className="flex-1 overflow-y-auto scrollbar-hide">
                <Stack className="gap-4 p-4">
                    {/* Pinned Alert Card */}
                    <PinnedAlertCard
                        alert={alert}
                        onActionClick={handleActionClick}
                        isLoading={isLoading}
                        disableActions={!!pendingAction}
                    />

                    <Divider label="Activity" labelPosition="center" className="my-2" />

                    {/* Chat Messages */}
                    <AnimatePresence mode="popLayout">
                        {chatMessages.map((msg) => {
                            if (msg.type === 'log') {
                                return (
                                    <ChatLogEntry
                                        key={msg.id}
                                        logType={msg.logType}
                                        message={msg.message}
                                        user={msg.user}
                                        timestamp={msg.timestamp}
                                    />
                                );
                            }

                            if (msg.type === 'action_result') {
                                return (
                                    <motion.div
                                        key={msg.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`p-3 rounded-lg border ${
                                            msg.success
                                                ? 'bg-green/5 border-green/30'
                                                : 'bg-halt-400/20 border-halt-800/30'
                                        }`}
                                    >
                                        <Text className={`text-sm font-medium ${msg.success ? 'text-green' : 'text-halt-800'}`}>
                                            {msg.success ? '✓' : '✗'} {msg.message}
                                        </Text>
                                    </motion.div>
                                );
                            }

                            return null;
                        })}
                    </AnimatePresence>

                    {/* Pending Action Card */}
                    {pendingAction && pendingActionDetails && (
                        <ActionDetailCard
                            action={pendingAction}
                            details={pendingActionDetails}
                            onConfirm={handleConfirmAction}
                            onReject={handleRejectAction}
                            onEdit={pendingAction.isEditable ? handleEditAction : undefined}
                            isLoading={isLoading}
                        />
                    )}

                    <div ref={messagesEndRef} />
                </Stack>
            </Box>

            {/* Delete Confirmation Modal */}
            <Modal
                opened={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title={
                    <Group className="gap-2">
                        <Trash size={20} weight="fill" className="text-halt-800" />
                        <Text className="font-semibold">Delete Alert</Text>
                    </Group>
                }
                centered
                size="sm"
            >
                <Stack className="gap-4">
                    <Text className="text-sm text-gray-700">
                        Are you sure you want to delete this alert? This action cannot be undone.
                    </Text>
                    <Group className="justify-end gap-3">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => setShowDeleteModal(false)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="red"
                            onClick={handleDelete}
                            loading={isDeleting}
                        >
                            Delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* Iframe Edit Modal */}
            <IframeEditModal
                opened={!!iframeUrl}
                url={iframeUrl || ''}
                action={iframeAction}
                actionDetails={iframeActionDetails}
                alertId={alert.id}
                onConfirm={handleIframeConfirm}
                onCancel={handleIframeCancel}
                isLoading={isLoading}
            />
        </Stack>
    );
});

ChatWindow.displayName = 'ChatWindow';

export default ChatWindow;
